import React from "react";
import {
  ArrowRight,
  Captions,
  ChevronRight,
  CirclePlay,
  Download,
  Focus,
  Link2,
  ScanLine,
  SlidersHorizontal,
} from "lucide-react";
import sourcePoster from "../../assets/how-it-works/source.jpg";
import clip1Poster from "../../assets/how-it-works/clip-1.jpg";
import clip2Poster from "../../assets/how-it-works/clip-2.jpg";
import clip3Poster from "../../assets/how-it-works/clip-3.jpg";

interface HowItWorksSectionProps {
  onOpenDetails?: () => void;
}

const outputClips = [
  { poster: clip1Poster, duration: "0:42" },
  { poster: clip2Poster, duration: "1:16" },
  { poster: clip3Poster, duration: "0:19" },
] as const;

const stages = [
  {
    icon: Link2,
    title: "Add a YouTube video",
    copy: "Drop in the link of the video you want to clip. It's that easy.",
  },
  {
    icon: SlidersHorizontal,
    title: "Pick your settings",
    copy: "Choose your preferred number of clips and length, and toggle captions and clip titles.",
  },
  {
    icon: Download,
    title: "Get viral-ready clips",
    copy: "TopClip gives you engaging clips that are ready to post.",
  },
] as const;

const StageCopy: React.FC<{ stage: (typeof stages)[number]; index: number }> = ({
  stage,
  index,
}) => {
  const Icon = stage.icon;
  return (
    <div
      data-how-it-works-copy
      className="order-first border-b border-slate-200 pb-5 lg:order-last lg:mt-5 lg:border-b-0 lg:border-t lg:pb-0 lg:pt-5"
    >
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3">
        <div className="grid h-11 w-11 flex-none place-items-center rounded-full border border-blue-200 bg-blue-50 text-[var(--tc-blue)]">
          <Icon aria-hidden="true" className="h-5 w-5" />
        </div>
        <div>
          <h3 className="flex min-h-11 items-center text-xl font-bold leading-7 text-[var(--tc-ink)]">
            {index + 1}. {stage.title}
          </h3>
          <p className="mt-2 text-base leading-7 text-[var(--tc-ink-muted)] sm:text-lg">
            {stage.copy}
          </p>
        </div>
      </div>
    </div>
  );
};

export const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({
  onOpenDetails,
}) => (
  <section
    id="how-it-works"
    className="bg-[#f7faff] px-5 py-20 sm:px-6 sm:py-28 lg:px-8"
    aria-labelledby="how-it-works-heading"
  >
    <div className="mx-auto max-w-7xl">
      <div className="mx-auto max-w-3xl text-center">
        <h2
          id="how-it-works-heading"
          className="tc-section-title text-[var(--tc-ink)]"
        >
          How TopClip works
        </h2>
      </div>

      <div
        className="tc-how-machine mt-10 overflow-hidden rounded-[2rem] border border-[#bfd1e5] bg-white shadow-[0_30px_80px_rgba(29,78,216,0.10)] sm:mt-12"
        aria-label="Source-to-output workflow preview"
      >
        <div className="cl-machine__body grid lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.18fr)_minmax(0,0.87fr)]">
          <article className="cl-source flex h-full flex-col border-b border-slate-200 p-5 sm:p-7 lg:border-b-0 lg:border-r">
            <StageCopy stage={stages[0]} index={0} />
            <div
              data-how-it-works-visual
              className="cl-stage-visual order-last flex flex-col justify-center pt-5 lg:order-first lg:min-h-[17rem] lg:pt-0"
            >
              <div className="cl-source__media relative aspect-video overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-[0_11px_24px_rgba(31,73,119,0.11)]">
                <img
                  src={sourcePoster}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                <span className="cl-source__play absolute bottom-3 left-3 grid h-9 w-9 place-items-center rounded-full border border-white/50 bg-slate-950/55 text-white backdrop-blur-sm">
                  <CirclePlay aria-hidden="true" className="h-5 w-5" />
                </span>
              </div>
            </div>
          </article>

          <article className="cl-cutline flex h-full flex-col border-b border-slate-200 p-5 sm:p-7 lg:border-b-0 lg:border-r">
            <StageCopy stage={stages[1]} index={1} />
            <div
              data-how-it-works-visual
              className="cl-stage-visual order-last flex flex-col justify-center pt-5 lg:order-first lg:min-h-[17rem] lg:pt-0"
            >
              <div className="cl-timeline relative h-32 overflow-hidden border-y border-[#b9cee2] bg-[repeating-linear-gradient(90deg,transparent_0,transparent_29px,rgba(36,79,123,0.08)_30px)]">
                <span className="cl-timeline__wave absolute inset-x-0 top-1/2 h-12 -translate-y-1/2" />
                <span className="cl-timeline__selection cl-timeline__selection--one absolute bottom-3 left-[22%] top-3 w-[17%] rounded-md border border-blue-400/50 bg-blue-400/15" />
                <span className="cl-timeline__selection cl-timeline__selection--two absolute bottom-3 left-[65%] top-3 w-[20%] rounded-md border border-blue-400/50 bg-blue-400/15" />
                <span className="cl-timeline__playhead absolute bottom-0 left-[48%] top-0 w-0.5 bg-[var(--tc-blue)] shadow-[0_0_0_3px_rgba(8,120,255,0.10)]" />
              </div>
              <div className="cl-cutline__steps mt-5 flex flex-wrap items-center justify-center gap-3 text-sm font-bold tracking-[0.04em] text-slate-600 sm:text-base">
                <span className="inline-flex items-center gap-1">
                  <ScanLine aria-hidden="true" className="h-5 w-5 text-blue-500" />
                  Find
                </span>
                <ChevronRight aria-hidden="true" className="h-5 w-5 text-slate-300" />
                <span className="inline-flex items-center gap-1">
                  <Focus aria-hidden="true" className="h-5 w-5 text-blue-500" />
                  Reframe
                </span>
                <ChevronRight aria-hidden="true" className="h-5 w-5 text-slate-300" />
                <span className="inline-flex items-center gap-1">
                  <Captions aria-hidden="true" className="h-5 w-5 text-blue-500" />
                  Caption
                </span>
              </div>
            </div>
          </article>

          <article className="cl-outputs flex h-full flex-col p-5 sm:p-7">
            <StageCopy stage={stages[2]} index={2} />
            <div
              data-how-it-works-visual
              className="cl-stage-visual order-last flex flex-col justify-center pt-5 lg:order-first lg:min-h-[17rem] lg:pt-0"
            >
              <div className="cl-output-rail grid grid-cols-3 gap-2">
                {outputClips.map((output) => (
                  <div
                    key={output.poster}
                    className="relative aspect-[9/16] overflow-hidden rounded-[0.65rem] border-2 border-white bg-slate-800 shadow-[0_7px_17px_rgba(25,60,101,0.13)]"
                  >
                    <img
                      src={output.poster}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute bottom-2 right-2 rounded bg-slate-950/70 px-1.5 py-0.5 text-xs font-bold text-white">
                      {output.duration}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </div>
      </div>

      {onOpenDetails && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={onOpenDetails}
            className="inline-flex min-h-14 items-center gap-3 rounded-2xl border border-blue-200 bg-white px-6 py-4 font-bold text-[var(--tc-blue)] shadow-[0_14px_34px_rgba(22,119,255,0.13)] transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:shadow-[0_18px_38px_rgba(22,119,255,0.18)]"
          >
            See the detailed flow
            <ArrowRight aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  </section>
);
